import { useState } from 'react';

import { Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Text } from '@backstage/ui';

const LONG_VALUE_THRESHOLD = 200;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
    marginTop: 'var(--bui-space-2)',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-1)',
  },
  value: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  valuePlaceholder: {
    color: 'var(--bui-fg-secondary)',
  },
  fallback: {
    marginTop: 'var(--bui-space-1)',
    padding: 'var(--bui-space-2)',
    backgroundColor: 'var(--bui-bg-surface-2)',
    borderRadius: 'var(--bui-radius-1)',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    color: 'var(--bui-fg-secondary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
  },
  toggle: {
    background: 'none',
    border: 'none',
    color: 'var(--bui-fg-secondary)',
    cursor: 'pointer',
    display: 'block',
    fontSize: 'var(--bui-font-size-1)',
    marginTop: 'var(--bui-space-1)',
    padding: 0,
    textAlign: 'left',
  },
});

const humanizeKey = (key: string): string => {
  const spaced = key.replace(/[_-]/g, ' ').trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
};

const isRenderableScalar = (
  value: unknown,
): value is string | number | boolean | null | undefined => {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
};

const StringValue = ({ value }: { value: string }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const isLong = value.length > LONG_VALUE_THRESHOLD;

  if (!isLong) {
    return (
      <Text variant="body-small" className={classes.value}>
        {value}
      </Text>
    );
  }

  return (
    <>
      {!expanded && (
        <Text variant="body-small" className={classes.value}>
          {value.slice(0, LONG_VALUE_THRESHOLD)}…
        </Text>
      )}
      <Collapse in={expanded} unmountOnExit>
        <Text variant="body-small" className={classes.value}>
          {value}
        </Text>
      </Collapse>
      <button
        type="button"
        className={classes.toggle}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Show less' : 'Show more'}
      </button>
    </>
  );
};

const ScalarValue = ({
  value,
}: {
  value: string | number | boolean | null | undefined;
}) => {
  const classes = useStyles();

  if (value === null || value === undefined) {
    return (
      <Text variant="body-small" className={classes.valuePlaceholder}>
        —
      </Text>
    );
  }

  if (typeof value === 'string') {
    return <StringValue value={value} />;
  }

  return (
    <Text variant="body-small" className={classes.value}>
      {String(value)}
    </Text>
  );
};

const JsonFallback = ({ input }: { input: unknown }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        className={classes.toggle}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Hide raw input' : 'Show raw input'}
      </button>
      <Collapse in={expanded} unmountOnExit>
        <pre className={classes.fallback}>{JSON.stringify(input, null, 2)}</pre>
      </Collapse>
    </div>
  );
};

export const ToolInputPreview = ({ input }: { input?: unknown }) => {
  const classes = useStyles();

  if (input === null || input === undefined) {
    return null;
  }

  if (!isPlainObject(input)) {
    return <JsonFallback input={input} />;
  }

  const entries = Object.entries(input);
  if (entries.length === 0) {
    return null;
  }

  const allRenderable = entries.every(([, value]) => isRenderableScalar(value));
  if (!allRenderable) {
    return <JsonFallback input={input} />;
  }

  return (
    <div className={classes.root}>
      {entries.map(([key, value]) => (
        <div key={key} className={classes.row}>
          <Text variant="body-small" color="secondary">
            {humanizeKey(key)}
          </Text>
          <ScalarValue
            value={value as string | number | boolean | null | undefined}
          />
        </div>
      ))}
    </div>
  );
};
